import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { authentication } from "@microsoft/teams-js";

import { APPREG_SCOPE, configuration } from "@/lib/auth";
import { teamsUserIdentity } from "@/lib/teams/host";

/**
 * Teams SSO token acquisition.
 *
 * NAA (Nested App Authentication) is the primary path: MSAL runs nested inside
 * the Teams host but mints tokens for OUR app registration + API scope, so the
 * access token carries `aud=api://<client_id>` — byte-for-byte the same
 * audience as the standalone browser flow. The backend therefore stays
 * unchanged (see `frontend/docs/teams-integration.md`, MA-131 ADR).
 *
 * The `authentication.getAuthToken()` fallback mints a token whose audience is
 * the manifest `webApplicationInfo.resource` (App ID URI). It requires the
 * manifest + pre-authorized Teams client IDs (MA-136 / MA-137), so it only
 * works once those land; NAA needs neither.
 *
 * TODO(MA-133+): route the app's own PublicClientApplication through NAA so the
 * existing MSAL React hooks (useMsal / AuthenticatedTemplate) reflect the Teams
 * session natively, instead of the dedicated nested instance used here.
 */

let nestedAppPromise: Promise<IPublicClientApplication> | undefined;

/**
 * The shared MSAL configuration plus `supportsNestedAppAuth`, which is what
 * opts this instance into the NAA protocol (MSAL brokers through the Microsoft
 * 365 host instead of running its own redirect/popup flow). Derived rather than
 * set on the shared `configuration` so the standalone browser instance in
 * `lib/auth.ts` keeps its current behaviour exactly.
 *
 * The app registration must also carry a matching `brk-multihub://<host>` SPA
 * redirect URI (managed in `Terraform/Infrastructure/appregistration.tf`) or the
 * broker refuses the request.
 *
 * The cache is left at MSAL's `sessionStorage` default on purpose. An earlier
 * version moved it to `localStorage`, reasoning that an interactively obtained
 * account would then survive the tab closing. That reasoning was wrong: the NAA
 * bridge never returns a refresh token -- `naa/TokenResponse` carries
 * `access_token`, `id_token` and nothing else, and `hydrateCache` stores only
 * those -- so the host broker keeps the refresh token and `localStorage` buys one
 * access-token lifetime, roughly an hour, not durable sign-in. Durability, if we
 * get it, comes from the broker's own cache rather than ours.
 *
 * Against that near-zero benefit: this tab's audience explicitly includes
 * *shared* Windows machines, where `localStorage` would leave a valid access
 * token for whoever sits down next.
 */
function nestedConfiguration(): Configuration {
  return {
    ...configuration,
    auth: { ...configuration.auth, supportsNestedAppAuth: true },
  };
}

function getNestedApp(): Promise<IPublicClientApplication> {
  if (!nestedAppPromise) {
    nestedAppPromise = createNestablePublicClientApplication(
      nestedConfiguration(),
    );
  }
  return nestedAppPromise;
}

/**
 * The account to name on a silent request.
 *
 * Prefers what MSAL already knows; falls back to a stub built from the Teams
 * context so the request still carries a `platformBrokerId`. Without one the
 * Windows broker must resolve a *default* account and refuses -- "The credential
 * is invalid. Default account not found." -- which is the failure this exists to
 * address. Returns undefined outside a Teams host, in which case the request goes
 * out account-less exactly as it did before.
 */
async function silentAccount(
  pca: IPublicClientApplication,
): Promise<AccountInfo | undefined> {
  const known = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (known) return known;

  const identity = await teamsUserIdentity();
  if (!identity) return undefined;

  const homeAccountId = `${identity.objectId}.${identity.tenantId}`;
  return (
    pca.getAccount({ homeAccountId }) ?? {
      homeAccountId,
      localAccountId: identity.objectId,
      tenantId: identity.tenantId,
      username: identity.username,
      environment: "login.microsoftonline.com",
    }
  );
}

async function acquireViaNaa(): Promise<string> {
  const pca = await getNestedApp();
  const scopes = [APPREG_SCOPE];

  // One call, not a branch on whether an account is known: under NAA `ssoSilent`
  // *is* `acquireTokenSilentInternal` -- `ssoSilent(request) { return
  // this.acquireTokenSilentInternal(request); }` -- so the old two-arm version
  // differed only in that one arm sent no account, which is the arm the broker
  // rejects. `account` is optional on SilentRequest, so an unresolvable one
  // degrades to the previous behaviour rather than erroring.
  const account = await silentAccount(pca);
  return (await pca.acquireTokenSilent({ scopes, account })).accessToken;
}

async function acquireViaGetAuthToken(): Promise<string> {
  // `resources` is load-bearing. Do not "simplify" it away -- that was tried, and
  // it broke this path twice over.
  //
  // It reads as redundant, and the parameter is even marked "@hidden @internal" in
  // teams-js, so the temptation is real. What actually happens when it is dropped
  // is that the host resolves the resource from the manifest's
  // `webApplicationInfo.resource` and validates that value against the tab's
  // origin:
  //
  //   "App resource defined in manifest and iframe origin do not match"
  //   IncorrectConfiguration
  //
  // That first failure is now fixed on the manifest side -- `resource` is
  // `api://<tab-host>/<client_id>`, the documented domain-qualified form -- but
  // fixing it makes passing `resources` *more* necessary, not less. The manifest
  // resource is also what the minted token's `aud` becomes, and the backend
  // accepts only the bare `api://<client_id>` (`AUTH_AZURE_APPREG_SCOPE`; see
  // frontend/docs/teams-integration.md §3). So without this parameter the host
  // would hand back a well-formed token for the wrong audience and the API would
  // 401 -- a quieter failure than the one it replaced.
  //
  // Passing the scope explicitly settles both: it bypasses the origin check and
  // pins `aud` to what the backend validates.
  return authentication.getAuthToken({ resources: [APPREG_SCOPE] });
}

/**
 * Whether a token failure is the recoverable "ask the user" kind.
 *
 * Not an `instanceof InteractionRequiredAuthError` check alone. On Windows the
 * broker refused with `StatusInternal::InteractionRequired` /
 * `oauth_error_code: interaction_required`, yet the old code's instanceof gate
 * never fired -- so whatever surfaced was not that class, and gating on the type
 * alone loses the very case it was meant to catch. Matching the codes as well
 * keeps the distinction that matters: a recoverable refusal earns a sign-in
 * prompt, while a dead bridge or a network failure must not pretend a button will
 * help.
 */
export function isInteractionRequired(e: unknown): boolean {
  if (e instanceof InteractionRequiredAuthError) return true;
  const parts = [
    typeof e === "object" && e !== null && "errorCode" in e
      ? String((e as { errorCode?: unknown }).errorCode)
      : "",
    e instanceof Error ? e.message : "",
  ].join(" ");
  return /interaction_?required|consent_?required|login_?required/i.test(parts);
}

/**
 * Acquire a token interactively, prompting the user.
 *
 * Deliberately NOT called automatically when the silent path fails. It must be
 * driven by a user gesture (see `TeamsAuthGate`), for two reasons:
 *
 * 1. A popup opened from page load, with no gesture behind it, is what popup
 *    blockers exist to stop. Firing it on load risks burning the one attempt
 *    that could have worked.
 * 2. A sign-in dialog appearing unbidden on tab load is worse than a button the
 *    user chooses to press -- and pressing it is unavoidable for this audience
 *    anyway (below).
 *
 * Who needs this: a user whose Windows is not signed in with their work account.
 * On Windows the Teams desktop client brokers tokens through the OS (WAM /
 * OneAuth), which needs a device identity and a Primary Refresh Token to answer
 * silently. Without them the broker returns `interaction_required` -- "The
 * credential is invalid. Default account not found." -- and no amount of
 * retrying silently will change that. Reproduced on a clean Windows 11 VM:
 * `AzureAdJoined: NO`, `AzureAdPrt: NO`, every attempt `silent: true`, all
 * failing. macOS, Linux and the browser are unaffected because none of them puts
 * the OS broker in the path -- the browser uses an Entra session cookie instead,
 * which is why the web client works for the very same user.
 */
export async function acquireTeamsAccessTokenInteractive(): Promise<string> {
  const pca = await getNestedApp();
  return (await pca.acquireTokenPopup({ scopes: [APPREG_SCOPE] })).accessToken;
}

/**
 * Acquire an access token for the backend API from inside a Teams tab, without
 * ever prompting: NAA silently, then the legacy Teams SSO `getAuthToken` flow.
 *
 * Stays prompt-free because this is also what every subsequent API call goes
 * through (`lib/auth.ts` `acquireAccessToken`), where there is no user gesture to
 * hang a dialog off. When both silent paths fail, it throws and the caller
 * decides -- which for the initial load means offering
 * {@link acquireTeamsAccessTokenInteractive} behind a button.
 */
export async function acquireTeamsAccessToken(): Promise<string> {
  try {
    return await acquireViaNaa();
  } catch (err) {
    console.warn(
      "[teams] NAA token acquisition failed; falling back to getAuthToken",
      err,
    );
    return acquireViaGetAuthToken();
  }
}
